import { useCallback, useEffect, useRef, useState } from "react";
import { useOptionalVmClient } from "@/api/context";
import type { AgentStatus } from "@/api/types";
import { useChatInputContext } from "@/contexts/ChatInputContext";
import {
  trackMessageSent,
  trackGenerationStopped,
  trackFileAttached,
} from "@/analytics";
import type { AttachedFile } from "./types";
import { validateFile } from "./validation";

interface UseChatInputParams {
  isRunning: boolean;
  agentStatus?: AgentStatus | "running";
  startupLabel?: string;
  showStartupStatus?: boolean;
  sendInFlight?: boolean;
  clearOnSubmit?: boolean;
  /** External block (e.g. payment-required) that disables the input. */
  blocked?: boolean;
}

export interface ChatInputState {
  message: string;
  onMessageChange: (value: string) => void;
  attachedFiles: AttachedFile[];
  onAttachFiles: (files: FileList | File[]) => Promise<void>;
  onRemoveFile: (id: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onStop: () => void;
  /** Restore the last submitted message into the input — invoked when
   *  the user presses ArrowUp on an empty input. No-op if there's no
   *  prior message or the input isn't empty. */
  recallLast: () => void;
  isUploading: boolean;
  hasAttachmentErrors: boolean;
  isBlocked: boolean;
  isSending: boolean;
  startupStatus: string | null;
  vmAvailable: boolean;
  selectionRef: React.MutableRefObject<{ start: number; end: number }>;
}

export const useChatInput = ({
  isRunning,
  agentStatus,
  startupLabel,
  showStartupStatus = false,
  sendInFlight = false,
  clearOnSubmit = true,
  blocked = false,
}: UseChatInputParams): ChatInputState => {
  const vmClient = useOptionalVmClient();
  const { message, setMessage, push, queue, selectionRef } = useChatInputContext();

  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);

  const isUploading = attachedFiles.some((f) => f.status === "uploading");
  const hasAttachmentErrors = attachedFiles.some((f) => f.status === "error");
  const isBlocked = blocked || agentStatus === "starting" || agentStatus === "stopping";
  const isSending = queue.some((c) => c.type === "send") || sendInFlight;

  // Dev commands (`$ …` / `$$ …`) round-trip on /api/chat but never
  // boot an agent, so suppress the "Starting your agent…" banner while
  // the latest submit is one of those.
  const lastSendWasDevRef = useRef(false);
  const showSendStartupStatus = isSending && !lastSendWasDevRef.current;
  const startupStatus =
    startupLabel && (showStartupStatus || showSendStartupStatus)
      ? startupLabel
      : null;

  const onAttachFiles = useCallback(
    async (fileList: FileList | File[]) => {
      if (!vmClient) return;
      const files = Array.from(fileList);
      trackFileAttached(files.length);
      const newEntries: AttachedFile[] = files.map((file) => {
        const error = validateFile(file);
        return {
          id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
          file,
          status: error ? ("error" as const) : ("uploading" as const),
          progress: 0,
          error: error ?? undefined,
        };
      });
      setAttachedFiles((prev) => [...prev, ...newEntries]);
      const validEntries = newEntries.filter((e) => e.status !== "error");

      const MAX_CONCURRENT = 4;
      let running = 0;
      let idx = 0;

      const uploadEntry = async (entry: AttachedFile) => {
        try {
          const result = await vmClient.uploadFile(entry.file, (percent) => {
            setAttachedFiles((prev) =>
              prev.map((f) =>
                f.id === entry.id ? { ...f, progress: percent } : f,
              ),
            );
          });
          setAttachedFiles((prev) =>
            prev.map((f) =>
              f.id === entry.id
                ? {
                    ...f,
                    status: "done" as const,
                    progress: 100,
                    path: result.path,
                  }
                : f,
            ),
          );
        } catch (err) {
          setAttachedFiles((prev) =>
            prev.map((f) =>
              f.id === entry.id
                ? {
                    ...f,
                    status: "error" as const,
                    error: err instanceof Error ? err.message : String(err),
                  }
                : f,
            ),
          );
        }
      };

      await new Promise<void>((resolve) => {
        if (validEntries.length === 0) { resolve(); return; }
        const next = () => {
          while (running < MAX_CONCURRENT && idx < validEntries.length) {
            running++;
            const entry = validEntries[idx++];
            uploadEntry(entry).finally(() => {
              running--;
              next();
            });
          }
          if (running === 0) resolve();
        };
        next();
      });
    },
    [vmClient],
  );

  const onRemoveFile = useCallback((id: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const onStop = useCallback(() => {
    trackGenerationStopped();
    push({ type: "stop" });
  }, [push]);

  // Last submitted message, for ArrowUp-on-empty recall. Saved *before*
  // any gating in onSubmit so a silently-dropped message (e.g. agent
  // off, queue full) is still recoverable.
  const lastSubmittedRef = useRef<string | null>(null);

  const recallLast = useCallback(() => {
    const last = lastSubmittedRef.current;
    if (last && !message) setMessage(last);
  }, [message, setMessage]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Save anything non-empty (whitespace counts) so the user can recall
    // exactly what they typed, even if it would have been trimmed away
    // by the send guard below.
    if (message) lastSubmittedRef.current = message;
    if (
      !message.trim() ||
      isRunning ||
      isSending ||
      isBlocked ||
      isUploading ||
      hasAttachmentErrors
    ) return;
    lastSendWasDevRef.current = message.startsWith('$');
    trackMessageSent();
    const doneFiles = attachedFiles.filter(
      (f) => f.status === "done" && f.path,
    );
    const filePaths = doneFiles.map((f) => f.path!);
    push({
      type: "send",
      message,
      filePaths: filePaths.length > 0 ? filePaths : undefined,
      clearOnAck: clearOnSubmit,
    });
    setAttachedFiles([]);
    // Mark a pending refocus; the effect below executes it once
    // `isSending` flips back to false. We can't focus inline because
    // the textarea is `disabled` while sending and browsers refuse
    // focus on disabled elements.
    refocusPendingRef.current = true;
  };

  // Refocus the chat input after every successful submit, once the
  // textarea is no longer disabled. Mobile + desktop ChatInput variants
  // both render at once (one CSS-hidden), so pick the visible one via
  // offsetParent.
  const refocusPendingRef = useRef(false);
  useEffect(() => {
    if (!refocusPendingRef.current) return;
    if (isSending || isBlocked) return;
    refocusPendingRef.current = false;
    const tas = document.querySelectorAll<HTMLTextAreaElement>('textarea');
    for (const ta of tas) {
      if (ta.offsetParent !== null && !ta.disabled) { ta.focus(); break; }
    }
  }, [isSending, isBlocked]);

  return {
    message,
    onMessageChange: setMessage,
    attachedFiles,
    onAttachFiles,
    onRemoveFile,
    onSubmit,
    onStop,
    recallLast,
    isUploading,
    hasAttachmentErrors,
    isBlocked,
    isSending,
    startupStatus,
    vmAvailable: vmClient !== null,
    selectionRef,
  };
};
